import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import Tabs from 'react-bootstrap/Tabs';
import Tab from 'react-bootstrap/Tab';

import WithIframe from '../../../Common/WithIframe/WithIframe';
import { lookerParams } from '../../../../shared/lookerParams';
import { useCompare } from '../../../../shared/hooks';

import LookerService from '../LookerDashboard/lookerService';
const lookerService = new LookerService();

//#region PropTypes
const propTypes = {
    customerId: PropTypes.number,
};

const defaultProps = {
    customerId: null,
};
//#endregion

const capitalizeString = (s) => {
    if (typeof s !== 'string') return '';
    if (s == 'vista_referral') {
        s = 'Vista Referral';
    }
    return s.charAt(0).toUpperCase() + s.slice(1);
};

const AnalyticsExplores = (props) => {
    const { customerId } = props;

    const [lookerExploreUrls, setLookerExploreUrls] = useState([]);
    const [loading, setLoading] = useState(true);

    const hasCustomerIdChanged = useCompare(customerId);

    const [currentActiveTab, setCurrentActiveTab] = useState('');

    // Handles Tab changes and ensures embed content is loaded when tab is clicked
    const tabChangeHandler = useCallback(
        (tabKey) => {
            if (lookerExploreUrls?.length) {
                const loadedExploreUrls = lookerExploreUrls.map((exploreUrl) => {
                    const { key, loaded } = exploreUrl;
                    if (key === tabKey && !loaded) {
                        return { ...exploreUrl, loaded: true };
                    }
                    return exploreUrl;
                });
                setLookerExploreUrls(loadedExploreUrls);
            }
            setCurrentActiveTab(tabKey);
        },
        [lookerExploreUrls],
    );

    const getEmbedUrls = useCallback(async () => {
        const { models, explores } = lookerParams.exploresLookerPath;

        // Guard: `models` and `explores` are baked in at build time from
        // REACT_APP_LOOKER_PROJECT_EXPLORES / REACT_APP_LOOKER_EXPLORES by the
        // ui-build-deploy workflow. If those vars are not injected for this
        // environment they are undefined here, which previously caused a crash on
        // `.split(',')` (UI) and an unhandled `.split` 500 in the API. Fail clearly.
        if (!models || !explores) {
            console.error(
                'AnalyticsExplores: missing Looker explore configuration. Ensure ' +
                'REACT_APP_LOOKER_PROJECT_EXPLORES and REACT_APP_LOOKER_EXPLORES are set ' +
                'in the ui-build-deploy workflow for this environment.',
                { models, explores },
            );
            setLoading(false);
            return;
        }

        const explorePaths = explores.split(',');
        const data = {
            lookerModel: models,
            lookerPaths: explorePaths.sort(),
            currentCustomerId: customerId,
        };

        try {
            // Only get Explore Urls if there are none and if role is ADMIN
            const res = await lookerService.getLookerExploreEmbeddedUrl(data);

            if (res && res.data) {
                console.log('getLookerExploreEmbeddedUrl', res);
                const { lookerExploreEmbeddedUrls } = res.data;
                const exploreUrls = explorePaths.map((explorePath, index) => ({
                    title: capitalizeString(explorePath),
                    url: lookerExploreEmbeddedUrls[index],
                    key: explorePath,
                    // Load default Explore Url
                    loaded: explorePath == 'prescription' ? true : false,
                }));

                setLookerExploreUrls(exploreUrls);

                explorePaths.map((explorePath) => {
                    setCurrentActiveTab(explorePath);
                });
                setCurrentActiveTab('prescription');
                setLoading(false);
            } else {
                console.log('getLookerExploreEmbeddedUrl Error : ', res);
            }
        } catch (error) {
            console.error(error);
        }
    }, [customerId]);

    useEffect(() => {
        if (customerId && hasCustomerIdChanged) {
            setLoading(true);
            // Set to empty array so iframe unloads from DOM
            setLookerExploreUrls(null);
            getEmbedUrls();
        }
    }, [customerId, hasCustomerIdChanged, getEmbedUrls]);

    return (
        <Tabs activeKey={currentActiveTab} onSelect={tabChangeHandler} className="pt-2 pl-3 bg-white">
            {!loading &&
                lookerExploreUrls.length &&
                lookerExploreUrls.map((exploreUrl) => (
                    <Tab
                        key={exploreUrl.key}
                        eventKey={exploreUrl.key}
                        style={{
                            textTransform: 'capitalize',
                        }}
                        title={exploreUrl.title}
                        className="analytics-explores-tab"
                    >
                        {exploreUrl?.loaded ? <WithIframe url={exploreUrl.url} key={exploreUrl.key} /> : null}
                    </Tab>
                ))}
        </Tabs>
    );
};

AnalyticsExplores.propTypes = propTypes;
AnalyticsExplores.defaultProps = defaultProps;

export default AnalyticsExplores;
